/**
 * ADR-0240 Slice 2b — typed client for the `/v1/agency/reports/*`
 * endpoints:
 *
 *   POST  /v1/agency/reports/bulk-import
 *   GET   /v1/agency/reports/bulk-import/:run_id
 *   GET   /v1/agency/reports/bulk-import/:run_id/export.csv  (Blob)
 *   POST  /v1/agency/reports/:row_id/send
 *   GET   /v1/agency/reports/runs        (UX follow-up 2026-10-10)
 *
 * Discriminated-union results mirror `agency-referral.ts`; the view
 * never reads raw ApiError codes — every failure maps to a plain
 * English sentence via `explainApiError`.
 */

import { AuthRateLimitError } from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

export type BulkRowStatus =
  | 'queued'
  | 'running'
  | 'done'
  | 'failed'
  | 'rate_limited'
  | 'invalid_domain';

export interface BulkRowWire {
  readonly row_id: string;
  readonly domain: string;
  readonly status: BulkRowStatus;
  readonly report_slug: string | null;
  readonly report_url: string | null;
  readonly signals_present: number | null;
  readonly signals_total: number | null;
  readonly outcome: 'checked' | 'unreachable' | 'unchecked' | null;
  readonly platform: string | null;
  readonly top_findings: readonly string[];
}

export interface BulkRunWire {
  readonly run_id: string;
  readonly agency_account_id: string;
  readonly created_at: string;
  readonly domains_submitted: number;
  readonly domains_accepted: number;
  readonly status: 'running' | 'completed' | 'cancelled';
  readonly rows: readonly BulkRowWire[];
  readonly remaining_budget?: number;
}

/**
 * ADR-0240 Portal v2 Slice 2 (2026-10-10) — per-row reason the
 * server returns for a line that was NOT persisted. Mirrors
 * `SkipReason` in `services/api/src/agency/bulk-scan.ts`.
 */
export type ImportSkipReason =
  | 'duplicate_in_file'
  | 'invalid_domain'
  | 'unsupported_scheme'
  | 'empty'
  | 'scanned_recently'
  | 'over_batch_limit';

export interface ImportSkippedRow {
  readonly input: string;
  readonly reason: ImportSkipReason;
}

export type SubmitBulkImportResult =
  | {
      readonly kind: 'ok';
      readonly run_id: string;
      readonly rows: readonly BulkRowWire[];
      readonly remaining_budget: number;
      readonly skipped: readonly ImportSkippedRow[];
    }
  | { readonly kind: 'empty' }
  | { readonly kind: 'too_many_domains'; readonly max: number }
  | { readonly kind: 'duplicate_domain' }
  /**
   * ADR-0263 §7.5 (as amended for security 2026-11-03) — batch bigger
   * than the remaining monthly scan pool. The whole upload is
   * refused; no rows persisted; the dashboard renders "Only {remaining}
   * of your monthly scans are left. Remove rows or upgrade."
   */
  | { readonly kind: 'monthly_scan_limit_reached'; readonly remaining: number; readonly message: string }
  | { readonly kind: 'rate_limited'; readonly message: string }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export interface SubmitBulkImportOpts {
  /** ADR-0240 Portal v2 Slice 2 — when true, domains the agency
   *  scanned to `done` in the last 7 days get re-scanned. Default
   *  (undefined / false) skips them with `scanned_recently`. */
  readonly rescan_recent?: boolean;
}

/** ADR-0240 Portal v2 Slice 7 — smart-import row with optional
 *  contact fields the Send dialog can prefill. */
export interface BulkImportRow {
  readonly domain: string;
  readonly email?: string | null;
  readonly company?: string | null;
  readonly contact_name?: string | null;
}

export async function submitBulkImport(
  http: HttpClient,
  input: readonly string[] | readonly BulkImportRow[],
  opts: SubmitBulkImportOpts = {},
): Promise<SubmitBulkImportResult> {
  const isRows = Array.isArray(input) && input.length > 0 && typeof (input as readonly unknown[])[0] === 'object';
  try {
    const body: Record<string, unknown> = isRows
      ? { rows: input }
      : { domains: input };
    if (opts.rescan_recent === true) body['rescan_recent'] = true;
    const res = await http.request<{
      run_id: string;
      rows: readonly BulkRowWire[];
      remaining_budget: number;
      skipped?: readonly ImportSkippedRow[];
    }>({
      method: 'POST',
      path: '/v1/agency/reports/bulk-import',
      body,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return {
      kind: 'ok',
      run_id: res.run_id,
      rows: res.rows,
      remaining_budget: res.remaining_budget,
      skipped: res.skipped ?? [],
    };
  } catch (err) {
    if (err instanceof AuthRateLimitError) {
      // ADR-0263 §7.5 as amended for security (2026-11-03) —
      // whole-upload refusal is served as a 429, which the http
      // client converts to AuthRateLimitError. Server body carries
      // `remaining`.
      if (err.server_reason === 'monthly_scan_limit_reached') {
        const remaining = extractRateLimitNumber(err, 'remaining') ?? 0;
        return {
          kind: 'monthly_scan_limit_reached',
          remaining,
          message: `Only ${remaining} of your monthly scans are left. Remove rows or upgrade.`,
        };
      }
      return { kind: 'rate_limited', message: explainRateLimit(err) };
    }
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
      if (err.kind === 'bad_request') {
        if (err.server_reason === 'too_many_domains') {
          return { kind: 'too_many_domains', max: 50 };
        }
        if (err.server_reason === 'empty') return { kind: 'empty' };
        if (err.server_reason === 'duplicate_domain') return { kind: 'duplicate_domain' };
      }
      // ADR-0240 Portal v2 Slice 2 — the server now maps unique
      // violations to `duplicate_domain` (400). Any residual 409
      // reaches us as a generic conflict; surface a specific copy
      // so no raw `conflict:409` can land in the UI.
      if (err.kind === 'conflict' && err.server_reason === 'duplicate_domain') {
        return { kind: 'duplicate_domain' };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

/** Pull a number field from an ApiError or AuthRateLimitError body. */
function extractRateLimitNumber(
  err: ApiError | AuthRateLimitError,
  key: string,
): number | null {
  const body = (err as unknown as { server_body?: Record<string, unknown> | null })
    .server_body;
  if (body !== null && body !== undefined) {
    const v = body[key];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return null;
}

export type PollBulkRunResult =
  | { readonly kind: 'ok'; readonly run: BulkRunWire }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function pollBulkRun(
  http: HttpClient,
  run_id: string,
): Promise<PollBulkRunResult> {
  try {
    const res = await http.request<BulkRunWire>({
      method: 'GET',
      path: `/v1/agency/reports/bulk-import/${encodeURIComponent(run_id)}`,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', run: res };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

/**
 * UX follow-up 2026-10-10 — recent runs + budget + display-name shape.
 * Loaded on Reports page mount so (1) the budget line shows before a
 * submit, (2) past runs survive a tab close, (3) the consent dialog's
 * copy renders the agency's own name.
 */
export interface BulkRunCountsWire {
  readonly done: number;
  readonly queued: number;
  readonly running: number;
  readonly failed: number;
  readonly rate_limited: number;
  readonly invalid_domain: number;
}

export interface BulkRunSummaryWire {
  readonly run_id: string;
  readonly created_at: string;
  readonly domains_submitted: number;
  readonly domains_accepted: number;
  readonly status: 'running' | 'completed' | 'cancelled';
  readonly counts: BulkRunCountsWire;
}

export interface AgencyReportsOverviewWire {
  readonly cap: number;
  readonly used: number;
  readonly remaining_budget: number;
  readonly display_name: string | null;
  readonly runs: readonly BulkRunSummaryWire[];
}

export type GetReportsOverviewResult =
  | { readonly kind: 'ok'; readonly overview: AgencyReportsOverviewWire }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function getReportsOverview(http: HttpClient): Promise<GetReportsOverviewResult> {
  try {
    const res = await http.request<AgencyReportsOverviewWire>({
      method: 'GET',
      path: '/v1/agency/reports/runs',
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', overview: res };
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'forbidden') {
      return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export type DownloadBulkCsvResult =
  | { readonly kind: 'ok'; readonly blob: Blob }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error'; readonly message: string };

export async function downloadBulkCsv(
  http: HttpClient,
  run_id: string,
): Promise<DownloadBulkCsvResult> {
  try {
    const blob = await http.requestBlob({
      method: 'GET',
      path: `/v1/agency/reports/bulk-import/${encodeURIComponent(run_id)}/export.csv`,
    });
    return { kind: 'ok', blob };
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'not_found') {
      return { kind: 'not_found' };
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export type SendRowReportResult =
  | { readonly kind: 'ok'; readonly report_url: string }
  | { readonly kind: 'consent_required' }
  | { readonly kind: 'invalid_email' }
  | { readonly kind: 'invalid_subject'; readonly rule: string }
  | { readonly kind: 'invalid_first_name'; readonly rule: string }
  | { readonly kind: 'row_not_ready' }
  | { readonly kind: 'blocked' }
  | { readonly kind: 'send_failed' }
  /**
   * ADR-0263 §7.5 as amended for security (2026-11-03) — a report
   * guard refused the send. `scope` tells which 2/month guard
   * tripped: `recipient` (per (agency, recipient email)) or
   * `domain` (per (agency, scanned domain) = founder's "2 per
   * scan"). Message is server-composed.
   */
  | {
      readonly kind: 'email_limit_reached';
      readonly scope: 'recipient' | 'domain' | null;
      readonly message: string;
    }
  | { readonly kind: 'rate_limited'; readonly message: string }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export interface SendRowReportInput {
  readonly email: string;
  readonly consent_attested: boolean;
  /** ADR-0240 Portal v2 Slice 7 — editable subject (1-120 chars,
   *  no URLs other than the scanned domain). */
  readonly subject?: string;
  /** ADR-0240 Portal v2 Slice 7 — recipient first name for the
   *  greeting (1-40 chars, letters/space/hyphen/apostrophe). */
  readonly first_name?: string;
}

export async function sendRowReport(
  http: HttpClient,
  row_id: string,
  input: SendRowReportInput,
): Promise<SendRowReportResult> {
  try {
    const body: Record<string, unknown> = {
      email: input.email,
      consent_attested: input.consent_attested,
    };
    if (typeof input.subject === 'string' && input.subject.length > 0) body['subject'] = input.subject;
    if (typeof input.first_name === 'string' && input.first_name.length > 0) body['first_name'] = input.first_name;
    const res = await http.request<{ ok: true; report_url: string }>({
      method: 'POST',
      path: `/v1/agency/reports/${encodeURIComponent(row_id)}/send`,
      body,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', report_url: res.report_url };
  } catch (err) {
    if (err instanceof AuthRateLimitError) {
      // ADR-0263 §7.5 as amended for security (2026-11-03) —
      // per-recipient monthly guard refusal. Server composes a
      // plain-English `message`; surface it verbatim so the UI
      // never shows a raw error code.
      if (err.server_reason === 'email_limit_reached') {
        const msg =
          extractRateLimitString(err, 'message')
            ?? 'You have already sent 2 reports to this domain this month.';
        const scope_raw = extractRateLimitString(err, 'scope');
        const scope =
          scope_raw === 'recipient' || scope_raw === 'domain'
            ? scope_raw
            : null;
        return { kind: 'email_limit_reached', scope, message: msg };
      }
      return { kind: 'rate_limited', message: explainRateLimit(err) };
    }
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
      if (err.kind === 'bad_request') {
        if (err.server_reason === 'consent_required') return { kind: 'consent_required' };
        if (err.server_reason === 'invalid_email') return { kind: 'invalid_email' };
        if (err.server_reason === 'invalid_subject') {
          return { kind: 'invalid_subject', rule: 'subject' };
        }
        if (err.server_reason === 'invalid_first_name') {
          return { kind: 'invalid_first_name', rule: 'first_name' };
        }
      }
      if (err.kind === 'conflict') {
        if (err.server_reason === 'row_not_ready') return { kind: 'row_not_ready' };
        if (err.server_reason === 'blocked') return { kind: 'blocked' };
      }
      // 502 send_failed is the opaque collapse of every non-sent
      // outcome that isn't a specific user-correctable error (see the
      // server-side route docstring for the full set).
      if (err.status === 502 && err.server_reason === 'send_failed') {
        return { kind: 'send_failed' };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

/** ADR-0240 Portal v2 Slice 6 — a single row in the Reports table.
 *  Mirrors `ReportRowWire` on the server. */
export interface ReportTableRow {
  readonly row_id: string;
  readonly run_id: string;
  readonly domain: string;
  readonly company: string | null;
  readonly contact_email: string | null;
  readonly contact_name: string | null;
  readonly status: BulkRowStatus;
  readonly report_slug: string | null;
  readonly report_url: string | null;
  readonly signals_present: number | null;
  readonly signals_total: number | null;
  readonly outcome: 'checked' | 'unreachable' | 'unchecked' | null;
  readonly platform: string | null;
  readonly top_findings: readonly string[];
  readonly queued_at: string;
  readonly completed_at: string | null;
  readonly last_sent_at: string | null;
  readonly retry_count: number;
  readonly last_retry_at: string | null;
}

export type ReportTableStatusFilter =
  | 'all'
  | 'ready'
  | 'scanning'
  | 'failed'
  | 'rate_limited'
  | 'emailed'
  | 'not_emailed';

export interface ListReportsRowsOpts {
  readonly page_size?: number;
  readonly cursor?: string | null;
  readonly status?: ReportTableStatusFilter;
  readonly min_score?: number | null;
  readonly max_score?: number | null;
  readonly from?: string | null;
  readonly to?: string | null;
  readonly q?: string | null;
  readonly run_id?: string | null;
}

export interface ListReportsRowsResult {
  readonly rows: readonly ReportTableRow[];
  readonly total: number;
  readonly next_cursor: string | null;
  readonly page_size: number;
}

export async function listReportsRows(
  http: HttpClient,
  opts: ListReportsRowsOpts = {},
): Promise<
  | { readonly kind: 'ok'; readonly result: ListReportsRowsResult }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'rate_limited'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string }
> {
  const params = new URLSearchParams();
  if (opts.page_size !== undefined) params.set('page_size', String(opts.page_size));
  if (opts.cursor !== null && opts.cursor !== undefined) params.set('cursor', opts.cursor);
  if (opts.status !== undefined && opts.status !== 'all') params.set('status', opts.status);
  if (opts.min_score !== null && opts.min_score !== undefined) {
    params.set('min_score', String(opts.min_score));
  }
  if (opts.max_score !== null && opts.max_score !== undefined) {
    params.set('max_score', String(opts.max_score));
  }
  if (opts.from !== null && opts.from !== undefined) params.set('from', opts.from);
  if (opts.to !== null && opts.to !== undefined) params.set('to', opts.to);
  if (opts.q !== null && opts.q !== undefined && opts.q.length > 0) params.set('q', opts.q);
  if (opts.run_id !== null && opts.run_id !== undefined) params.set('run_id', opts.run_id);
  const qs = params.toString();
  try {
    const res = await http.request<ListReportsRowsResult>({
      method: 'GET',
      path: `/v1/agency/reports/rows${qs.length > 0 ? `?${qs}` : ''}`,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', result: res };
  } catch (err) {
    if (err instanceof AuthRateLimitError) {
      return { kind: 'rate_limited', message: explainRateLimit(err) };
    }
    if (err instanceof ApiError && err.kind === 'forbidden') {
      return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

/**
 * ADR-0240 Portal v2 §6.4 — download the current Reports-table
 * filter result as a CSV blob. Mirrors `ListReportsRowsOpts` filters
 * plus a `include_contact` opt-in that appends `email` +
 * `contact_name` columns.
 */
export interface ExportReportsCsvOpts extends Omit<ListReportsRowsOpts, 'page_size' | 'cursor'> {
  readonly include_contact?: boolean;
}

export async function exportReportsRowsCsv(
  http: HttpClient,
  opts: ExportReportsCsvOpts = {},
): Promise<
  | { readonly kind: 'ok'; readonly blob: Blob }
  | { readonly kind: 'rate_limited'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string }
> {
  const params = new URLSearchParams();
  if (opts.status !== undefined && opts.status !== 'all') params.set('status', opts.status);
  if (opts.min_score !== null && opts.min_score !== undefined) {
    params.set('min_score', String(opts.min_score));
  }
  if (opts.max_score !== null && opts.max_score !== undefined) {
    params.set('max_score', String(opts.max_score));
  }
  if (opts.from !== null && opts.from !== undefined) params.set('from', opts.from);
  if (opts.to !== null && opts.to !== undefined) params.set('to', opts.to);
  if (opts.q !== null && opts.q !== undefined && opts.q.length > 0) params.set('q', opts.q);
  if (opts.run_id !== null && opts.run_id !== undefined) params.set('run_id', opts.run_id);
  if (opts.include_contact === true) params.set('include_contact', '1');
  const qs = params.toString();
  try {
    const blob = await http.requestBlob({
      method: 'GET',
      path: `/v1/agency/reports/rows/export.csv${qs.length > 0 ? `?${qs}` : ''}`,
    });
    return { kind: 'ok', blob };
  } catch (err) {
    if (err instanceof AuthRateLimitError) {
      return { kind: 'rate_limited', message: explainRateLimit(err) };
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export interface CheckExistingResultRow {
  readonly domain: string;
  readonly row_id: string;
  readonly completed_at: string;
  readonly report_slug: string | null;
  readonly report_url: string | null;
}

export async function checkExistingDomains(
  http: HttpClient,
  domains: readonly string[],
): Promise<
  | { readonly kind: 'ok'; readonly existing: readonly CheckExistingResultRow[] }
  | { readonly kind: 'too_many'; readonly max: number }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string }
> {
  if (domains.length === 0) return { kind: 'ok', existing: [] };
  try {
    const res = await http.request<{ existing: readonly CheckExistingResultRow[] }>({
      method: 'POST',
      path: '/v1/agency/reports/check-existing',
      body: { domains },
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', existing: res.existing };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'bad_request' && err.server_reason === 'too_many_domains') {
        return { kind: 'too_many', max: 500 };
      }
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export type RetryRowResult =
  | { readonly kind: 'ok'; readonly retry_count: number }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'row_not_retryable'; readonly current_status: BulkRowStatus }
  | { readonly kind: 'row_too_young_for_rescan'; readonly min_age_hours: number }
  | { readonly kind: 'per_row_limit_reached'; readonly limit: number; readonly reset_at: string }
  | {
      readonly kind: 'hourly_limit_reached';
      readonly limit: number;
      readonly retry_after_seconds: number;
      readonly reset_at: string;
    }
  | {
      readonly kind: 'daily_limit_reached';
      readonly cap: number;
      readonly retry_after_seconds: number;
      readonly reset_at: string;
    }
  /**
   * ADR-0263 §7.5 as amended for security (2026-11-03) — single
   * retry/add-and-scan refusal against the monthly scan pool.
   * Message pre-rendered by the client so the UI never shows raw
   * error codes.
   */
  | {
      readonly kind: 'monthly_scan_limit_reached';
      readonly used: number;
      readonly limit: number;
      readonly reset_at_iso: string;
      readonly message: string;
    }
  | { readonly kind: 'rate_limited'; readonly message: string }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function retryReportRow(
  http: HttpClient,
  row_id: string,
): Promise<RetryRowResult> {
  try {
    const res = await http.request<{ ok: true; retry_count: number }>({
      method: 'POST',
      path: `/v1/agency/reports/${encodeURIComponent(row_id)}/retry`,
      body: {},
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', retry_count: res.retry_count };
  } catch (err) {
    if (err instanceof AuthRateLimitError) {
      // 429 body-reason branches. The http client surfaces 429s as
      // AuthRateLimitError with `server_body` carrying the parsed
      // body (ADR-0263 §7.5).
      if (err.server_reason === 'retry_limit_reached') {
        return {
          kind: 'hourly_limit_reached',
          limit: 20,
          retry_after_seconds: 60,
          reset_at: '',
        };
      }
      if (err.server_reason === 'per_row_limit_reached') {
        return { kind: 'per_row_limit_reached', limit: 3, reset_at: '' };
      }
      if (err.server_reason === 'agency_daily_limit_reached') {
        return {
          kind: 'daily_limit_reached',
          cap: 50,
          retry_after_seconds: 60,
          reset_at: '',
        };
      }
      // ADR-0263 §7.5 as amended for security (2026-11-03) —
      // retry refusal against the monthly scan pool. Carries
      // `used`, `limit`, `reset_at_iso` on the wire.
      if (err.server_reason === 'monthly_scan_limit_reached') {
        const used = extractRateLimitNumber(err, 'used') ?? 0;
        const limit = extractRateLimitNumber(err, 'limit') ?? 0;
        const reset_at_iso =
          extractRateLimitString(err, 'reset_at_iso')
            ?? extractRateLimitString(err, 'reset_at')
            ?? '';
        const resetLabel = formatResetLabel(reset_at_iso);
        const message = `${used.toLocaleString('en-US')} of ${limit.toLocaleString('en-US')} scans used this month. Resets ${resetLabel}.`;
        return {
          kind: 'monthly_scan_limit_reached',
          used,
          limit,
          reset_at_iso,
          message,
        };
      }
      // Fallthrough: shared rate-limited copy.
      return { kind: 'rate_limited', message: explainRateLimit(err) };
    }
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'conflict') {
        if (err.server_reason === 'row_not_retryable') {
          return {
            kind: 'row_not_retryable',
            current_status: 'failed',
          };
        }
        if (err.server_reason === 'row_too_young_for_rescan') {
          return { kind: 'row_too_young_for_rescan', min_age_hours: 24 };
        }
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

/** Pull a string field from an ApiError or AuthRateLimitError body. */
function extractRateLimitString(
  err: ApiError | AuthRateLimitError,
  key: string,
): string | null {
  const body = (err as unknown as { server_body?: Record<string, unknown> | null })
    .server_body;
  if (body !== null && body !== undefined) {
    const v = body[key];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return null;
}

/** Format an ISO-8601 reset timestamp as "on Dec 1" (same locale
 *  posture as the rest of the Agency Portal copy). */
function formatResetLabel(iso: string): string {
  if (iso.length === 0) return 'next month';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'next month';
  const d = new Date(t);
  const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  const day = d.getUTCDate();
  return `on ${month} ${day}`;
}

/**
 * ADR-0240 Agency Portal network-error fix (2026-10-10) — Remove a
 * row from the Reports table.
 *
 *   - Only `invalid_domain` + `failed` rows are removable. The UI
 *     only shows the Remove button on those; a stale click lands
 *     here and gets a 409 `row_not_removable` with the current
 *     status.
 *   - The server returns `{ ok: true }` on success; the UI deletes
 *     the row from local state without a round-trip refetch.
 */
export type RemoveRowResult =
  | { readonly kind: 'ok' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'row_not_removable'; readonly current_status: BulkRowStatus }
  | { readonly kind: 'rate_limited'; readonly message: string }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

/** v4 Agency Portal UX (2026-10-10) — bulk-delete result shape. */
export type BulkRemoveRowsResult =
  | {
      readonly kind: 'ok';
      readonly removed_count: number;
      readonly skipped_count: number;
      readonly removed: ReadonlyArray<{ readonly row_id: string; readonly domain: string }>;
      readonly skipped: ReadonlyArray<{
        readonly row_id: string;
        readonly reason: 'row_not_found' | 'row_not_removable';
        readonly current_status?: BulkRowStatus;
      }>;
    }
  | { readonly kind: 'empty' }
  | { readonly kind: 'too_many_rows'; readonly max: number }
  | { readonly kind: 'rate_limited'; readonly message: string }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function bulkRemoveReportRows(
  http: HttpClient,
  row_ids: readonly string[],
): Promise<BulkRemoveRowsResult> {
  try {
    const res = await http.request<{
      removed_count: number;
      skipped_count: number;
      removed: ReadonlyArray<{ row_id: string; domain: string }>;
      skipped: ReadonlyArray<{
        row_id: string;
        reason: 'row_not_found' | 'row_not_removable';
        current_status?: BulkRowStatus;
      }>;
    }>({
      method: 'POST',
      path: '/v1/agency/reports/rows/bulk-delete',
      body: { row_ids },
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return {
      kind: 'ok',
      removed_count: res.removed_count,
      skipped_count: res.skipped_count,
      removed: res.removed,
      skipped: res.skipped,
    };
  } catch (err) {
    if (err instanceof AuthRateLimitError) {
      return { kind: 'rate_limited', message: explainRateLimit(err) };
    }
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
      if (err.kind === 'bad_request') {
        if (err.server_reason === 'empty') return { kind: 'empty' };
        if (err.server_reason === 'too_many_rows') return { kind: 'too_many_rows', max: 100 };
      }
      if (err.status === 429 && err.server_reason === 'agency_burst_limit_reached') {
        return { kind: 'rate_limited', message: 'Too many requests. Wait a minute and try again.' };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export async function removeReportRow(
  http: HttpClient,
  row_id: string,
): Promise<RemoveRowResult> {
  try {
    const res = await http.request<{ ok: true }>({
      method: 'DELETE',
      path: `/v1/agency/reports/rows/${encodeURIComponent(row_id)}`,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok' };
  } catch (err) {
    if (err instanceof AuthRateLimitError) {
      return { kind: 'rate_limited', message: explainRateLimit(err) };
    }
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'conflict' && err.server_reason === 'row_not_removable') {
        // The server returns `current_status` on the conflict body;
        // the http client surfaces it via `server_reason` + the
        // `conflict` kind. `BulkRowStatus` is the closed set so a
        // defensive narrow keeps the UI union sound.
        return { kind: 'row_not_removable', current_status: 'failed' };
      }
      if (err.status === 429 && err.server_reason === 'agency_burst_limit_reached') {
        return {
          kind: 'rate_limited',
          message: 'Too many requests in a short time. Wait a minute and try again.',
        };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

/** Shared with other agency surfaces (see `agency.ts`). Maps the
 *  `agency_daily_limit_reached` 429 body to the plain English copy. */
function explainRateLimit(err: AuthRateLimitError): string {
  if (err.server_reason === 'agency_daily_limit_reached') {
    const hours = Math.max(1, Math.ceil(err.retry_after_minutes / 60));
    return `You've reached today's limit for client invites and access requests. It resets in about ${hours} ${hours === 1 ? 'hour' : 'hours'}. Contact Sill to raise it.`;
  }
  // ADR-0263 §7.5 as amended for security (2026-11-03) — per-recipient
  // monthly guards. The server composes a precise plain-English
  // message; surface it verbatim so no raw code shows.
  if (err.server_reason === 'email_limit_reached') {
    const srv_body = (err as unknown as { server_body?: Record<string, unknown> | null })
      .server_body;
    if (srv_body !== null && srv_body !== undefined) {
      const m = srv_body['message'];
      if (typeof m === 'string' && m.length > 0 && m.length < 400) return m;
    }
    return 'You have already sent 2 reports to this domain this month.';
  }
  return 'Too many requests in a short time. Wait a minute and try again.';
}

function explainApiError(err: unknown): string {
  if (err instanceof AuthRateLimitError) return explainRateLimit(err);
  if (err instanceof ApiError) {
    switch (err.kind) {
      case 'service_unavailable':
        return 'Service is temporarily unavailable. Try again shortly.';
      case 'network_timeout':
        return 'This may have saved — refresh to check before trying again.';
      case 'network_error':
        return 'Network error — check your connection.';
      case 'bad_response':
        return 'Server returned an unexpected response.';
      case 'forbidden':
        return 'You do not have permission to do this.';
      case 'not_found':
        return 'That report run was not found.';
      default:
        return err.server_reason ?? err.message;
    }
  }
  return err instanceof Error ? err.message : 'Network error.';
}
